export interface HighlightSpan {
  text: string;
  isHighlighted: boolean;
  start: number;
  end: number;
}

/**
 * Escapes special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Unescapes a string by handling both quote and backslash escapes
 */
function unescapeString(str: string): string {
  return str
    .replace(/\\"/g, '"') // Unescape quotes
    .replace(/\\\\/g, "\\"); // Unescape backslashes
}

/**
 * Extracts plain search terms from filters (excludes filter:value patterns)
 */
export function extractSearchTermsFromFilters(filters: {
  searchTerms: string[];
}): string[] {
  const terms: string[] = [];

  for (const term of filters.searchTerms) {
    // Skip filter:value patterns
    if (term.includes(":") && !term.startsWith('"') && !term.startsWith('-"')) {
      continue;
    }

    // Handle quoted terms
    if (term.startsWith('"') && term.endsWith('"')) {
      const unquoted = unescapeString(term.slice(1, -1));
      if (unquoted) terms.push(unquoted);
    }
    // Handle exclusion terms (skip them for highlighting)
    else if (term.startsWith("-")) {
      continue;
    }
    // Regular terms (also need unescaping for cases like foo\\)
    else if (term) {
      const unescaped = unescapeString(term);
      terms.push(unescaped);
    }
  }

  return terms;
}

/**
 * Splits text into spans with highlighting information
 */
export function getHighlightedSpans(
  text: string,
  searchTerms: string[],
  caseSensitive: boolean = false,
): HighlightSpan[] {
  if (!searchTerms.length || !text) {
    return [
      {
        text,
        isHighlighted: false,
        start: 0,
        end: text.length,
      },
    ];
  }

  // Create a regex pattern that matches any of the search terms
  const pattern = searchTerms
    .filter((term) => term.length > 0)
    .map((term) => `(${escapeRegex(term)})`)
    .join("|");

  if (!pattern) {
    return [
      {
        text,
        isHighlighted: false,
        start: 0,
        end: text.length,
      },
    ];
  }

  const regex = new RegExp(pattern, caseSensitive ? "g" : "gi");
  const spans: HighlightSpan[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add non-highlighted span before the match
    if (match.index > lastIndex) {
      spans.push({
        text: text.slice(lastIndex, match.index),
        isHighlighted: false,
        start: lastIndex,
        end: match.index,
      });
    }

    // Add highlighted span for the match
    spans.push({
      text: match[0],
      isHighlighted: true,
      start: match.index,
      end: match.index + match[0].length,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining non-highlighted text
  if (lastIndex < text.length) {
    spans.push({
      text: text.slice(lastIndex),
      isHighlighted: false,
      start: lastIndex,
      end: text.length,
    });
  }

  return spans;
}
